import { cn } from '@utils/cn';
import React from 'react';
import { useTranslation } from '@hooks/useTranslation';

export type EquipmentStatus =
  | 'AVAILABLE'
  | 'IN_USE'
  | 'MAINTENANCE'
  | 'RESERVED'
  | 'RETIRED';

interface EquipmentStatusBadgeProps {
  status: EquipmentStatus;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const EquipmentStatusBadge: React.FC<EquipmentStatusBadgeProps> = ({
  status,
  size = 'md',
  className = '',
}) => {
  // Sprint 29, story-148. Colors/variants unchanged — text-only fix (UX design's own
  // instruction). Falls back to `common.unknown` for any status not in this exhaustive map,
  // never a raw `.replace()`/enum value, per the house pattern in `@utils/taskDisplay`.
  const tEnumLabels = useTranslation('enumLabels');
  const tCommon = useTranslation('common');

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs h-5',
    md: 'px-3 py-1 text-sm h-6',
    lg: 'px-4 py-1.5 text-base h-8',
  };

  const statusConfig: Record<
    EquipmentStatus,
    { label: string; classes: string; dotColor: string }
  > = {
    AVAILABLE: {
      label: tEnumLabels.equipmentStatusAvailable,
      classes: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100',
      dotColor: 'bg-green-500',
    },
    IN_USE: {
      label: tEnumLabels.equipmentStatusInUse,
      classes: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100',
      dotColor: 'bg-blue-500',
    },
    MAINTENANCE: {
      label: tEnumLabels.equipmentStatusMaintenance,
      classes: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100',
      dotColor: 'bg-yellow-500',
    },
    RESERVED: {
      label: tEnumLabels.equipmentStatusReserved,
      classes: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-100',
      dotColor: 'bg-purple-500',
    },
    RETIRED: {
      label: tEnumLabels.equipmentStatusRetired,
      classes: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100',
      dotColor: 'bg-gray-500',
    },
  };

  const config = statusConfig[status] ?? {
    label: tCommon.unknown,
    classes: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100',
    dotColor: 'bg-gray-500',
  };
  const baseClasses =
    'inline-flex items-center justify-center gap-1.5 rounded-full font-medium whitespace-nowrap w-fit';

  return (
    <span className={cn(baseClasses, sizeClasses[size], config.classes, className)}>
      <span className={cn('w-2 h-2 rounded-full', config.dotColor)} />
      {config.label}
    </span>
  );
};
